import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../auth';
import { ErrorNotice, Spinner, formatDate, formatMoney } from '../components/ui';
import type { DashboardData } from '../types';

/**
 * What a committee member sees when they sit down.
 *
 * Things needing attention first, and only the ones that are actually true.
 * An item that is always on the page is one nobody reads by the third visit,
 * so when nothing is wrong this says so rather than inventing something.
 *
 * Everything links to the screen where the thing gets done. The server has
 * already filtered by permission, so a mentoring volunteer's copy of this
 * carries no figures at all.
 */
export function DashboardPage() {
  const { identity } = useAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    api.get<DashboardData>('/dashboard').then(setData).catch(setError);
  }, []);

  const firstName = identity?.member.first_name ?? '';

  return (
    <>
      <div className="page-head">
        <h1>{firstName ? `Good day, ${firstName}` : 'Dashboard'}</h1>
        {data?.year ? (
          <p className="muted">
            {data.year.year} membership year — subscriptions due {formatDate(data.year.due_on)}
          </p>
        ) : null}
      </div>

      <ErrorNotice error={error} />
      {data === null && error === null ? <Spinner label="Working out what needs doing" /> : null}

      {data !== null ? (
        <>
          {data.attention.length === 0 ? (
            <div className="card">
              <p className="nothing-doing">Nothing needs your attention.</p>
              <p className="muted small">
                Everything the system can check looks in order. Have a cup of tea.
              </p>
            </div>
          ) : (
            <div className="card">
              <h2>Needs attention</h2>
              <ul className="attention">
                {data.attention.map((item) => (
                  <li key={item.kind} className={`attention-${item.severity}`}>
                    <div className="attention-body">
                      <strong>{item.title}</strong>
                      <p className="muted small">{item.detail}</p>
                    </div>
                    {item.link !== null ? (
                      <Link to={item.link} className="button-link">
                        {item.link_label}
                      </Link>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Only for people who may see subscriptions — the server leaves
              this out entirely for anybody else. */}
          {data.figures !== null ? (
            <div className="card">
              <h2>Where {data.year?.year} stands</h2>
              <div className="figures">
                <div className="figure">
                  <span className="figure-value">{data.figures.members}</span>
                  <span className="figure-label">members</span>
                </div>
                <div className="figure">
                  <span className="figure-value">{data.figures.settled}</span>
                  <span className="figure-label">paid up</span>
                </div>
                <div className="figure">
                  <span className="figure-value">{formatMoney(data.figures.outstanding)}</span>
                  <span className="figure-label">still owed</span>
                </div>
              </div>
              <p className="muted small">
                <Link to="/subscriptions">Who still owes</Link> ·{' '}
                <Link to="/reports/agm">The full AGM figures</Link>
              </p>
            </div>
          ) : null}
        </>
      ) : null}
    </>
  );
}
